import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import type { LegacyRowActionsConfigProp } from '@ctw/shared/components/tables/legacy-table';
import { useAddConditionForm } from '@ctw/shared/content/conditions/use-add-condition-form';
import { useToggleRead } from '@ctw/shared/content/hooks/use-toggle-read';
import type { WritebackAction } from '@ctw/shared/content/resource/resource-title-column';
import { useCallback, useState } from 'react';

export function useConditionRowActions(
  isWritebackEnabled: boolean,
  onWritebackStart: (action: WritebackAction, id: string) => void,
  onWritebackEnd: (
    action: WritebackAction,
    id: string,
    isError: boolean,
    errorMessage?: string,
  ) => void,
): (r: ConditionModel) => LegacyRowActionsConfigProp<ConditionModel> {
  const { toggleRead } = useToggleRead();

  const [writebackInProgress, setWritebackInProgress] = useState<Record<string, boolean>>({});

  const showAddConditionForm = useAddConditionForm(
    (id: string) => {
      onWritebackStart('add', id);
      setWritebackInProgress((prev) => ({ ...prev, [id]: true }));
    },
    (id: string, isError: boolean, errorMessage?: string) => {
      onWritebackEnd('add', id, isError, errorMessage);
      setWritebackInProgress((prev) => ({ ...prev, [id]: false }));
    },
  );

  return useCallback(
    (record: ConditionModel): LegacyRowActionsConfigProp<ConditionModel> => {
      if (!isWritebackEnabled) {
        return [];
      }

      if (!(record.syncedWithRecord || writebackInProgress[record.id])) {
        return [
          {
            className:
              'rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-sm btn bg-primary-main text-white hover:bg-primary-text',
            text: 'Add to Record',
            onClick: () => {
              if (!record.isRead) {
                void toggleRead(record);
              }
              showAddConditionForm(record);
            },
          },
        ];
      }

      return [];
    },
    [isWritebackEnabled, showAddConditionForm, toggleRead, writebackInProgress],
  );
}
